import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, ShoppingBag, User, Menu, X, Sparkles, Send, Heart,
  Instagram, MapPin, Phone, Mail, ShieldCheck, Truck, RotateCcw, Gem, ChevronDown, MessageCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useStore, API } from "./store";

export const WHATSAPP_NUMBER = "9911298518";
export const WHATSAPP_LINK = `https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi Eloire team, I have a question about your jewellery.")}`;
export const SUPPORT_EMAIL = "support@eloire.in";
export const INSTAGRAM_LINK = "https://www.instagram.com/eloire.in/";

const WOMEN_SUBS = [
  { slug: "necklaces", label: "Necklaces" },
  { slug: "earrings", label: "Earrings" },
  { slug: "rings", label: "Rings" },
  { slug: "bangles-bracelets", label: "Bangles & Bracelets" },
  { slug: "anklets", label: "Anklets" },
  { slug: "mangalsutra-pendant", label: "Mangalsutra & Pendants" },
];
const MEN_SUBS = [
  { slug: "chains", label: "Chains" },
  { slug: "rings", label: "Rings" },
  { slug: "kada", label: "Bracelets & Kada" },
  { slug: "cufflinks", label: "Cufflinks" },
  { slug: "pendants", label: "Pendants" },
];

export function Navbar() {
  const { cartCount, user, wishlist } = useStore();
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [hoverMenu, setHoverMenu] = useState(null);
  const nav = useNavigate();

  const submitSearch = (e) => {
    e?.preventDefault();
    const q = searchQ.trim();
    if (!q) return;
    setShowSearch(false); setSearchQ("");
    nav(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <>
      <div className="text-center text-xs py-2" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
        <span className="opacity-90">Free shipping on orders above ₹2000 · 7-day easy returns · WhatsApp us at +91-9911298518</span>
      </div>
      <header className="glass-header sticky top-0 z-40" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <button className="md:hidden" onClick={() => setOpen(v => !v)} data-testid="mobile-menu-btn" aria-label="menu">
            {open ? <X size={22}/> : <Menu size={22}/>}
          </button>

          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-serif text-2xl md:text-3xl tracking-tighter" style={{ color: "var(--primary)" }}>Eloire</span>
            <span className="overline hidden md:inline" style={{ color: "var(--secondary)" }}>Jewels</span>
          </Link>

          <nav className="hidden md:flex items-center gap-10 text-sm">
            <NavItem label="Women" to="/shop/women" subs={WOMEN_SUBS} basePath="/shop/women" hover={hoverMenu} setHover={setHoverMenu} testid="nav-women"/>
            <Link to="/collection/royal" className="hover:opacity-70 flex items-center gap-1.5" data-testid="nav-royal">
              <Gem size={14} style={{color:"var(--secondary)"}}/> Eloire Royal
            </Link>
            <NavItem label="Men" to="/shop/men" subs={MEN_SUBS} basePath="/shop/men" hover={hoverMenu} setHover={setHoverMenu} testid="nav-men"/>
            <Link to="/collection/wedding" className="hover:opacity-70" data-testid="nav-wedding">Wedding</Link>
          </nav>

          <div className="flex items-center gap-5">
            <button onClick={() => setShowSearch(true)} data-testid="search-btn" aria-label="search"><Search size={18}/></button>
            <Link to="/wishlist" className="relative" data-testid="wishlist-link" aria-label="wishlist">
              <Heart size={18}/>
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center text-white" style={{ background: "var(--secondary)" }}>{wishlist.length}</span>
              )}
            </Link>
            <Link to={user ? "/account" : "/login"} data-testid="account-link" aria-label="account"><User size={18}/></Link>
            <Link to="/cart" className="relative" data-testid="cart-link" aria-label="cart">
              <ShoppingBag size={18}/>
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] rounded-full h-4 min-w-4 px-1 flex items-center justify-center text-white" style={{ background: "var(--secondary)" }} data-testid="cart-count-badge">{cartCount}</span>
              )}
            </Link>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {open && (
            <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="md:hidden overflow-hidden border-t" style={{ borderColor: "var(--border)", background: "white" }}>
              <div className="flex flex-col p-6 gap-2">
                <details className="border-b pb-2" style={{borderColor:"var(--border)"}}>
                  <summary className="cursor-pointer py-2 flex justify-between items-center">Women <ChevronDown size={14}/></summary>
                  {WOMEN_SUBS.map(s => (<Link key={s.slug} to={`/shop/women/${s.slug}`} onClick={() => setOpen(false)} className="block py-1.5 pl-4 text-sm opacity-80">{s.label}</Link>))}
                </details>
                <Link to="/collection/royal" onClick={() => setOpen(false)} className="py-2 border-b" style={{borderColor:"var(--border)"}}>Eloire Royal</Link>
                <details className="border-b pb-2" style={{borderColor:"var(--border)"}}>
                  <summary className="cursor-pointer py-2 flex justify-between items-center">Men <ChevronDown size={14}/></summary>
                  {MEN_SUBS.map(s => (<Link key={s.slug} to={`/shop/men/${s.slug}`} onClick={() => setOpen(false)} className="block py-1.5 pl-4 text-sm opacity-80">{s.label}</Link>))}
                </details>
                <Link to="/collection/wedding" onClick={() => setOpen(false)} className="py-2">Wedding</Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Search overlay */}
      <AnimatePresence>
        {showSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-center pt-24" style={{ background: "rgba(11,40,31,0.6)", backdropFilter: "blur(10px)" }}
            onClick={() => setShowSearch(false)} data-testid="search-overlay">
            <motion.form onClick={e => e.stopPropagation()} onSubmit={submitSearch}
              initial={{ y: -30 }} animate={{ y: 0 }} exit={{ y: -30 }}
              className="w-[90vw] max-w-2xl bg-white p-6 shadow-2xl">
              <div className="overline mb-3" style={{color:"var(--secondary)"}}>Find your piece</div>
              <div className="flex items-center gap-3">
                <Search size={20}/>
                <input autoFocus value={searchQ} onChange={e => setSearchQ(e.target.value)}
                  placeholder="Search rings, necklaces, kada..."
                  className="flex-1 bg-transparent text-xl outline-none py-2 border-b" style={{borderColor:"var(--border)"}}
                  data-testid="search-input"/>
                <button type="submit" className="btn-gold text-sm px-4 py-2" data-testid="search-submit">Search</button>
                <button type="button" onClick={() => setShowSearch(false)}><X size={20}/></button>
              </div>
              <div className="mt-4 text-xs opacity-60">Try: "gold jhumka", "kada", "diamond ring", "mangalsutra"</div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

function NavItem({ label, to, subs, basePath, hover, setHover, testid }) {
  const active = hover === label;
  return (
    <div onMouseEnter={() => setHover(label)} onMouseLeave={() => setHover(null)} className="relative">
      <Link to={to} className="flex items-center gap-1 hover:opacity-70 py-4" data-testid={testid}>
        {label} <ChevronDown size={12} className="opacity-60"/>
      </Link>
      <AnimatePresence>
        {active && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 min-w-56 shadow-xl border" style={{ background: "white", borderColor: "var(--border)" }}>
            <div className="py-2">
              <Link to={to} className="block px-5 py-2 text-sm font-medium hover:bg-[var(--muted)]" style={{color:"var(--primary)"}} data-testid={`${testid}-all`}>All {label}</Link>
              <div className="border-t my-1" style={{borderColor:"var(--border)"}}/>
              {subs.map(s => (
                <Link key={s.slug} to={`${basePath}/${s.slug}`} className="block px-5 py-2 text-sm hover:bg-[var(--muted)]" data-testid={`${testid}-${s.slug}`}>{s.label}</Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


export function Footer() {
  return (
    <footer className="relative mt-24 grain" style={{ background: "var(--primary)", color: "var(--primary-fg)" }} data-testid="site-footer">
      {/* Trust strip */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
          <div className="flex items-center gap-3"><ShieldCheck size={22} style={{color:"var(--secondary)"}}/><div><div className="font-medium">Assured Quality</div><div className="text-xs opacity-70">Certified craftsmanship</div></div></div>
          <div className="flex items-center gap-3"><Truck size={22} style={{color:"var(--secondary)"}}/><div><div className="font-medium">Free Shipping</div><div className="text-xs opacity-70">Above ₹2000, pan-India</div></div></div>
          <div className="flex items-center gap-3"><RotateCcw size={22} style={{color:"var(--secondary)"}}/><div><div className="font-medium">7-day Returns</div><div className="text-xs opacity-70">Easy & hassle-free</div></div></div>
          <div className="flex items-center gap-3"><Gem size={22} style={{color:"var(--secondary)"}}/><div><div className="font-medium">Handcrafted</div><div className="text-xs opacity-70">Made with love in India</div></div></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 grid md:grid-cols-6 gap-10">
        <div className="md:col-span-2">
          <div className="font-serif text-4xl tracking-tighter">Eloire</div>
          <p className="text-sm opacity-70 mt-4 leading-relaxed max-w-sm">Modern heirlooms, handcrafted with a whisper of tradition — for the soul that seeks legacy. Made in Delhi, India.</p>
          <div className="mt-6">
            <div className="overline mb-3" style={{color:"var(--secondary)"}}>Join The Insider List</div>
            <form className="flex gap-2 max-w-xs" onSubmit={e => e.preventDefault()}>
              <input placeholder="your@email.com" className="flex-1 px-3 py-2 text-sm bg-white/10 border border-white/20 text-white placeholder-white/50 outline-none" data-testid="footer-newsletter-input"/>
              <button className="text-xs px-4 py-2 font-medium" style={{background:"var(--secondary)", color:"white"}} data-testid="footer-newsletter-submit">Subscribe</button>
            </form>
          </div>
          <div className="flex gap-3 mt-6 opacity-80 items-center flex-wrap">
            <a href={INSTAGRAM_LINK} target="_blank" rel="noopener noreferrer" aria-label="Follow us on Instagram" data-testid="footer-instagram-link" className="hover:opacity-100 transition-opacity">
              <Instagram size={18}/>
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" aria-label="Chat with us on WhatsApp" data-testid="footer-whatsapp-link" className="hover:opacity-100 transition-opacity">
              <MessageCircle size={18}/>
            </a>
            <a href="https://facebook.com/profile.php?id=61591876690978" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Facebook" data-testid="footer-facebook-link" className="hover:opacity-100 transition-opacity">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </a>
            <a href="https://x.com/Eloire_jewels" target="_blank" rel="noopener noreferrer" aria-label="Follow us on X (Twitter)" data-testid="footer-x-link" className="hover:opacity-100 transition-opacity">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://pin.it/5sIUVEHQW" target="_blank" rel="noopener noreferrer" aria-label="Follow us on Pinterest" data-testid="footer-pinterest-link" className="hover:opacity-100 transition-opacity">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.098.119.112.224.083.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.746-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12.017 0z"/></svg>
            </a>
            <a href="https://youtube.com/@eloirein?si=d2TeZ-AQOxfY4H-2" target="_blank" rel="noopener noreferrer" aria-label="Subscribe to our YouTube channel" data-testid="footer-youtube-link" className="hover:opacity-100 transition-opacity">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
            </a>
          </div>
        </div>

        <div>
          <div className="overline mb-4" style={{color:"var(--secondary)"}}>Shop</div>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/shop/women">Women</Link></li>
            <li><Link to="/shop/men">Men</Link></li>
            <li><Link to="/collection/royal">Eloire Royal</Link></li>
            <li><Link to="/collection/wedding">Wedding</Link></li>
            <li><Link to="/collection/new-arrival">New Arrivals</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4" style={{color:"var(--secondary)"}}>Company</div>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/about">About Eloire</Link></li>
            <li><Link to="/contact">Contact Us</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/returns">Return & Exchange</Link></li>
            <li><Link to="/track">Track Order</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4" style={{color:"var(--secondary)"}}>Policies</div>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/policy/privacy">Privacy Policy</Link></li>
            <li><Link to="/policy/shipping">Shipping Policy</Link></li>
            <li><Link to="/policy/returns">Return & Refund Policy</Link></li>
            <li><Link to="/policy/terms">Terms & Conditions</Link></li>
          </ul>
        </div>

        <div>
          <div className="overline mb-4" style={{color:"var(--secondary)"}}>Get in Touch</div>
          <ul className="space-y-2 text-sm opacity-80">
            <li className="flex items-start gap-2"><MapPin size={14} className="mt-1"/> Delhi, India</li>
            <li className="flex items-center gap-2"><Phone size={14}/> <a href="tel:+919911298518">+91-9911298518</a></li>
            <li className="flex items-center gap-2"><MessageCircle size={14}/> <a href={WHATSAPP_LINK} target="_blank" rel="noopener">WhatsApp us</a></li>
            <li className="flex items-center gap-2"><Mail size={14}/> <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-wrap justify-between items-center gap-4 text-xs opacity-70">
          <div>© 2026 Eloire Jewels · Delhi, India. All rights reserved.</div>
          <div className="flex items-center gap-2">
            <span className="opacity-60">We accept:</span>
            <span>VISA</span>·<span>Mastercard</span>·<span>UPI</span>·<span>Razorpay</span>·<span>COD</span>
          </div>
        </div>
      </div>
    </footer>
  );
}


// Floating WhatsApp button (always visible above Concierge)
export function WhatsAppFloating() {
  return (
    <a href={WHATSAPP_LINK} target="_blank" rel="noopener"
       data-testid="whatsapp-floating-btn"
       className="fixed bottom-6 left-6 z-50 flex items-center justify-center w-14 h-14 rounded-full shadow-xl"
       style={{background:"#25D366"}} aria-label="WhatsApp us">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
      </svg>
    </a>
  );
}


// ============ AI Concierge Widget ============
export function ConciergeWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "bot", text: "Namaste ✨ I'm Eloire Concierge. How may I assist with your jewelry journey today?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => "sess-" + Math.random().toString(36).slice(2, 12));
  const scrollRef = useRef(null);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages(m => [...m, { role: "user", text }, { role: "bot", text: "", streaming: true }]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop() || "";
        for (const p of parts) {
          if (!p.startsWith("data:")) continue;
          try {
            const j = JSON.parse(p.slice(5).trim());
            if (j.text) {
              setMessages(m => {
                const copy = [...m]; const last = copy[copy.length - 1];
                if (last.role === "bot") copy[copy.length - 1] = { ...last, text: last.text + j.text };
                return copy;
              });
            } else if (j.done) {
              setMessages(m => { const copy = [...m]; copy[copy.length - 1] = { ...copy[copy.length - 1], streaming: false }; return copy; });
            } else if (j.error) {
              setMessages(m => { const copy = [...m]; copy[copy.length - 1] = { role: "bot", text: "Sorry, I couldn't reach the concierge. Please try again." }; return copy; });
            }
          } catch {}
        }
      }
    } catch {
      setMessages(m => { const copy = [...m]; copy[copy.length - 1] = { role: "bot", text: "Connection error. Please try again." }; return copy; });
    } finally { setBusy(false); }
  };

  return (
    <>
      <motion.button data-testid="concierge-open-btn" onClick={() => setOpen(v => !v)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 shadow-xl"
        style={{ background: "var(--primary)", color: "var(--primary-fg)", border: "1px solid var(--secondary)" }}
        whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }}>
        <Sparkles size={16} style={{ color: "var(--secondary)" }}/>
        <span className="text-sm tracking-wide">{open ? "Close" : "Concierge"}</span>
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.25 }}
            data-testid="concierge-window"
            className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-md h-[65vh] flex flex-col shadow-2xl"
            style={{ background: "rgba(255,255,255,0.92)", backdropFilter: "blur(18px)", border: "1px solid var(--border)" }}>
            <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)", background: "var(--primary)", color: "var(--primary-fg)" }}>
              <div className="flex items-center gap-2">
                <Sparkles size={16} style={{ color: "var(--secondary)" }}/>
                <div>
                  <div className="font-serif text-xl leading-none">Eloire Concierge</div>
                  <div className="text-[10px] opacity-70 mt-1">AI-powered • Replies in seconds</div>
                </div>
              </div>
              <button onClick={() => setOpen(false)} data-testid="concierge-close-btn"><X size={18}/></button>
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${i}`}>
                  <div className="max-w-[80%] px-4 py-2.5 text-sm leading-relaxed"
                    style={{ background: m.role === "user" ? "var(--primary)" : "var(--muted)", color: m.role === "user" ? "var(--primary-fg)" : "var(--text)" }}>
                    {m.text}{m.streaming && <span className="inline-block ml-1 opacity-60">▍</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t flex items-center gap-2" style={{ borderColor: "var(--border)" }}>
              <input data-testid="concierge-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
                placeholder="Ask about products, orders, care..." className="flex-1 px-4 py-2.5 text-sm border bg-white" style={{ borderColor: "var(--border)" }}/>
              <button data-testid="concierge-send-btn" onClick={send} disabled={busy} className="p-2.5"
                style={{ background: "var(--secondary)", color: "white" }}><Send size={16}/></button>
            </div>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener" data-testid="concierge-whatsapp-link"
              className="block text-center py-3 text-xs border-t hover:bg-[var(--muted)] transition"
              style={{borderColor:"var(--border)"}}>
              💬 Prefer to chat on WhatsApp? <span style={{color:"var(--secondary)"}} className="font-medium underline">Continue on WhatsApp →</span>
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
